"use client";

import { getLocaleConstants, saveFile, useNotifications } from "@tosize/ui";
import { usePathname } from "next/navigation";
import {
	type FC,
	type PropsWithChildren,
	type SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { match } from "ts-pattern";
import {
	type CMDKPage,
	CommandContext,
	type CommandDialogType,
} from "~/app/[lang]/command/Context";
import type { LangParams } from "~/app/[lang]/layout";
import { usePathnameChange } from "~/hooks/useRouteChange";
import { LOCALSTORAGE_COOKIE_KEY } from "../CookieConsent";
import { resetCart } from "../cart/actions";
import { createImportCsv, createSharableCart } from "../cart/actions/share";

interface Props extends LangParams {
	cartId?: string;
	messages: {
		copyCart: string;
		createCart: string;
		shareCart: string;
		newCart: string;
		shareCartDescription: string;
		quantity: string;
		length: string;
		width: string;
		label: string;
		exportCart: string;
	};
}

export const CommandProvider: FC<PropsWithChildren<Props>> = (props) => {
	const { lang, cartId, messages, children } = props;
	const { notify, remove } = useNotifications();
	const localeConstants = getLocaleConstants(lang);
	const pathname = usePathname();

	const [pages, setPages] = useState<CMDKPage[]>(["home"]);
	const [open, onOpenChange] = useState(false);

	const onOpenChangeHandler = useCallback(
		(open?: boolean) => {
			onOpenChange((prev) => open ?? !prev);

			setPages(pathname.includes("/cart") ? ["home", "cart"] : ["home"]);

			if (open === false) {
				setInputValue("");
				setActiveDialog(undefined);
			}
		},
		[pathname],
	);

	usePathnameChange(() => onOpenChangeHandler(false));

	const [inputValue, setInputValue] = useState("");

	const activePage = useMemo(() => pages[pages.length - 1], [pages]);

	const [activeDialog, setInnerActiveDialog] = useState<CommandDialogType>();

	function isDialogActive(value: CommandDialogType) {
		return match(value)
			.with("postcode", () => localeConstants.postcode)
			.otherwise(() => true);
	}

	const setActiveDialog = useCallback((value: SetStateAction<CommandDialogType | undefined>) => {
		const localCookie =
			typeof window === "undefined" ? false : localStorage.getItem(LOCALSTORAGE_COOKIE_KEY);
		const cookieModalOpen = localCookie !== "granted" && localCookie !== "denied";

		if (!cookieModalOpen) {
			setInnerActiveDialog(value);
		}
	}, []);

	const copyCartCallback = useCallback(async () => {
		if (cartId) {
			await window.navigator.clipboard.writeText(cartId);

			onOpenChangeHandler(false);

			notify({
				variant: "info",
				title: messages.copyCart,
				subtitle: cartId,
			});
		}
	}, [cartId, notify, messages.copyCart, onOpenChangeHandler]);

	const shareCartCallback = useCallback(async () => {
		if (cartId) {
			const notifyId = Date.now();

			onOpenChangeHandler(false);

			notify({
				variant: "info",
				title: messages.createCart,
				identifier: notifyId,
			});

			await createSharableCart({ lang })
				.catch((e) => {
					if (e instanceof Error) {
						notify({ variant: "danger", title: e.message });
					}
				})
				.then(async (result) => {
					if (result?.data) {
						remove(notifyId);

						await window.navigator.clipboard.writeText(result.data);

						notify({
							variant: "success",
							title: messages.shareCart,
							subtitle: messages.shareCartDescription,
						});
					}
				});
		}
	}, [
		cartId,
		lang,
		notify,
		messages.createCart,
		messages.shareCartDescription,
		messages.shareCart,
		remove,
		onOpenChangeHandler,
	]);

	const newCartCallback = useCallback(async () => {
		if (cartId) {
			await resetCart();

			onOpenChangeHandler(false);

			notify({
				variant: "info",
				title: messages.newCart,
			});
		}
	}, [cartId, notify, messages.newCart, onOpenChangeHandler]);

	const saveProjectCallback = useCallback(async () => {
		setActiveDialog((prev) => (prev === "saveProject" ? undefined : "saveProject"));
		onOpenChange(false);
	}, [setActiveDialog]);

	const importProductsCallback = useCallback(async () => {
		setActiveDialog((prev) =>
			prev?.startsWith("importProducts") ? undefined : "importProducts_list",
		);
		onOpenChange(false);
	}, [setActiveDialog]);

	const exportCartToCSVCallback = useCallback(async () => {
		return await createImportCsv({ lang })
			.catch((e) => {
				if (e instanceof Error) {
					notify({ variant: "danger", title: e.message });
				}
			})
			.then(async (result) => {
				if (result?.data) {
					const csvRows = [
						`${messages.quantity},${messages.length},${messages.width},${messages.label}`,
						...result.data.split("\r\n"),
					].filter(Boolean);

					const csvContent = `data:text/csv;charset=utf-8,${csvRows.join("\r\n")}`;

					const fileName = `cart-${cartId}.csv`;
					saveFile(csvContent, fileName);

					notify({
						variant: "info",
						title: messages.exportCart,
						subtitle: fileName,
					});
				}
			});
	}, [
		cartId,
		lang,
		messages.exportCart,
		messages.label,
		messages.length,
		messages.quantity,
		messages.width,
		notify,
	]);

	const onKeyDown = useCallback(
		(event: KeyboardEvent) => {
			const inputs = ["input", "textarea"];
			const tagName =
				event.target instanceof HTMLElement ? event.target.tagName.toLowerCase() : undefined;
			const notFormElement = (tagName && !inputs.includes(tagName)) || !tagName;

			if (notFormElement || open) {
				if ((event.metaKey || event.ctrlKey) && event.key === "k") {
					onOpenChangeHandler();
				}

				if (event.key === "Backspace") {
					if (inputValue.length > 0) return;

					if (activePage === "home") {
						onOpenChangeHandler(false);
					} else {
						setPages((p) => p.slice(0, -1));
					}
				}

				if (event.key === "Enter") {
					setInputValue("");
				}

				if (event.shiftKey && event.key === "C") {
					void copyCartCallback();
				}

				if (event.shiftKey && event.key === "D") {
					void shareCartCallback();
				}

				if (event.shiftKey && event.key === "S") {
					void saveProjectCallback();
				}

				if (event.shiftKey && event.key === "N") {
					void newCartCallback();
				}

				if (event.shiftKey && event.key === "M") {
					void importProductsCallback();
				}

				if (event.shiftKey && event.key === "F") {
					void exportCartToCSVCallback();
				}
			}
		},
		[
			activePage,
			copyCartCallback,
			exportCartToCSVCallback,
			importProductsCallback,
			onOpenChangeHandler,
			inputValue.length,
			newCartCallback,
			open,
			saveProjectCallback,
			shareCartCallback,
		],
	);

	useEffect(() => {
		window.addEventListener("keydown", onKeyDown);

		return () => {
			window.removeEventListener("keydown", onKeyDown);
		};
	}, [onKeyDown]);

	return (
		<CommandContext.Provider
			value={{
				open,
				onOpenChangeHandler,
				inputValue,
				setInputValue,
				pages,
				setPages,
				activePage,
				copyCartCallback,
				shareCartCallback,
				newCartCallback,
				saveProjectCallback,
				importProductsCallback,
				exportCartToCSVCallback,
				isDialogActive,
				activeDialog,
				setActiveDialog,
			}}
		>
			{children}
		</CommandContext.Provider>
	);
};
